import React from 'react';
import { X, ArrowRight, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';

const ToolInstructionsPanel = ({ 
  tool, 
  isOpen, 
  onClose, 
  isAuthenticated,
  onOpenTool,
  onSignUp 
}) => {
  if (!isOpen || !tool) return null;

  const toolContent = {
    'merge': {
      title: 'Merge PDF',
      icon: '📄',
      intro: 'Combine multiple PDF files into a single, clean document in seconds.',
      steps: [
        'Upload your files',
        'Arrange in the order you want',
        'Click "Merge"'
      ]
    },
    'split': {
      title: 'Split PDF',
      icon: '✂️',
      intro: 'Extract specific pages or split your PDF into separate files quickly.',
      steps: [
        'Upload your PDF',
        'Select pages or ranges',
        'Click "Split"'
      ]
    },
    'compress': {
      title: 'Compress PDF',
      icon: '📦',
      intro: 'Reduce the file size of your PDF while keeping it clean and readable.',
      steps: [
        'Upload your PDF',
        'Choose compression level',
        'Download optimized file'
      ]
    },
    'convert-to-jpg': {
      title: 'PDF to JPG',
      icon: '🖼️',
      intro: 'Convert each page of your PDF into high-quality JPG images.',
      steps: [
        'Upload your PDF',
        'Choose output settings (optional)',
        'Download JPG images'
      ]
    },
    'pdf-to-word': {
      title: 'PDF to Word',
      icon: '📝',
      intro: 'Convert your PDF into a fully editable Word document with clean formatting.',
      steps: [
        'Upload your PDF file',
        'Choose layout options (preserve layout / text-only extraction)',
        'Click "Convert"',
        'Download your DOCX file instantly'
      ]
    },
    'word-to-pdf': {
      title: 'Word to PDF',
      icon: '📄',
      intro: 'Convert your Word documents into clean, professional PDF files with a single click.',
      steps: [
        'Upload your Word (.docx) file',
        'Adjust layout options (optional)',
        'Click "Convert"',
        'Download your PDF instantly'
      ]
    },
    'xfa-to-pdf': {
      title: 'XFA to PDF',
      icon: '🔄',
      intro: 'Some PDF forms (XFA forms) won\'t open in most browsers or apps. Use this tool to convert any XFA form into a normal PDF that is easy to view, print, and upload.',
      steps: [
        'Upload your XFA-based PDF',
        'The tool automatically detects the XFA structure',
        'Click "Convert to Standard PDF"',
        'Download your converted PDF — now compatible with all PDF readers'
      ]
    },
    'image-to-pdf': {
      title: 'Image to PDF',
      icon: '🖼️',
      intro: 'Easily convert JPG, JPEG, or PNG images into a clean, shareable PDF file.',
      steps: [
        'Upload one or more images (JPG, JPEG, or PNG)',
        'Arrange the order (optional)',
        'Click "Convert to PDF"',
        'Download your new PDF instantly'
      ]
    },
    'pdf-to-ppt': {
      title: 'PDF to PPT',
      icon: '📊',
      intro: 'Convert slide-format PDFs back into editable PowerPoint (.pptx) files. Works best with PDFs that were originally exported from PowerPoint, Keynote, Google Slides, or similar presentation tools. If your PDF is not in slide format, the tool will let you know and stop safely — nothing will be converted.',
      steps: [
        'Upload a slide-format PDF',
        'We automatically verify it looks like a presentation',
        'Click "Convert to PPTX"',
        'Download your new PowerPoint file'
      ]
    }
  };

  const content = toolContent[tool];
  if (!content) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Centered Modal */}
      <div 
        className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      >
        <Card 
          className={`relative w-full max-w-lg rounded-2xl shadow-2xl border-t-4 border-blue-500 transform transition-all duration-300 ${
            isOpen ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
          }`}
          onClick={(e) => e.stopPropagation()}
          style={{ maxHeight: 'calc(100vh - 40px)' }}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 z-10 p-2 rounded-full bg-white/80 dark:bg-slate-800/80 hover:bg-white dark:hover:bg-slate-700 shadow-md transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-slate-600 dark:text-slate-300" />
          </button>

          {/* Tool Header */}
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-slate-800 dark:to-slate-900 rounded-t-2xl pb-4 pt-5 pr-12">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-2xl shadow-lg flex-shrink-0">
                {content.icon}
              </div>
              <div>
                <CardTitle className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
                  {content.title}
                </CardTitle>
                <CardDescription className="text-slate-600 dark:text-slate-400 text-sm">
                  How to Use
                </CardDescription>
              </div>
            </div>
          </CardHeader>

          {/* Scrollable Content */}
          <CardContent className="p-4 sm:p-5 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 280px)' }}>
            {/* Introduction */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed text-sm sm:text-base">
                {content.intro}
              </p>
            </div>

            {/* Steps */}
            <div>
              <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-3">
                Quick Steps
              </h3>
              <div className="space-y-2">
                {content.steps.map((step, index) => (
                  <div 
                    key={`${content.title}-step-${index}-${step.slice(0, 20)}`}
                    className="flex items-start gap-3 p-2.5 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
                  >
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold">
                      {index + 1}
                    </div>
                    <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
                      {step}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>

          {/* Fixed Footer with CTA Button - Always Visible */}
          <div className="p-4 sm:p-5 pt-3 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-b-2xl">
            {isAuthenticated ? (
              <Button
                onClick={onOpenTool}
                size="lg"
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg h-12"
              >
                <span className="font-semibold">Open Tool</span>
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            ) : (
              <div className="space-y-2">
                <Button
                  onClick={onSignUp}
                  size="lg"
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg h-12"
                >
                  <Lock className="w-5 h-5 mr-2" />
                  <span className="font-semibold">Login / Sign Up to Use Tool</span>
                </Button>
                <p className="text-xs text-center text-slate-500 dark:text-slate-400">
                  Free account • 3 files/day • No credit card required
                </p>
              </div>
            )}
          </div>
        </Card>
      </div>
    </>
  );
};

export default ToolInstructionsPanel;
